import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, UserPlus, Trophy, Users } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { UserAvatar, UserLink } from "@/components/UserBits";
import { SkyCanvas } from "@/components/sky/SkyCanvas";
import { SkyDonate } from "@/components/sky/SkyDonate";
import { StarInfo } from "@/components/sky/StarInfo";
import { BoostBanner } from "@/components/sky/BoostBanner";

const fmt = (n) => n.toLocaleString("de-DE");

function Progress({ s }) {
  const pct = Math.min(100, (s.total / s.full) * 100);
  return (
    <div className="space-y-1.5" data-testid="sky-progress">
      <div className="flex justify-between text-xs font-semibold text-indigo-100"><span>{fmt(s.total)} / {fmt(s.full)} Sternenstaub</span><span>{pct.toFixed(1)} %</span></div>
      <div className="h-3 rounded-full bg-white/10 overflow-hidden"><div className="h-full rounded-full bg-gradient-to-r from-pink-400 via-amber-200 to-pink-300 sky-bar" style={{ width: `${pct}%` }} /></div>
    </div>
  );
}

function Builders({ top, meId }) {
  if (!top.length) return <p className="p-6 text-center text-sm text-slate-400" data-testid="sky-top-empty">Noch niemand hat Sternenstaub verschenkt – zünde den ersten Stern an!</p>;
  return (
    <ol className="divide-y divide-pink-50" data-testid="sky-top">
      {top.map((r) => (
        <li key={r.user_id} className={`flex items-center gap-3 p-4 ${r.user_id === meId ? "bg-rose-50" : ""}`} data-testid={`sky-top-${r.place}`}>
          <span className="w-6 text-center font-display font-extrabold text-slate-400">{r.place}</span>
          <UserAvatar name={r.name} picture={r.picture} userId={r.user_id} />
          <p className="flex-1 min-w-0 font-semibold truncate"><UserLink id={r.user_id}>{r.name}</UserLink></p>
          <span className="font-display font-extrabold text-indigo-900">{fmt(r.amount)} <span className="text-[10px] uppercase text-slate-400">Staub</span></span>
        </li>
      ))}
    </ol>
  );
}

export default function Sky() {
  const [s, setS] = useState(null);
  const [sel, setSel] = useState(null);
  const { user } = useAuth();
  useEffect(() => { api.get("/sky").then(({ data }) => setS(data)).catch(() => setS(false)); }, []);
  if (s === null) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-rose-500" /></div>;
  if (!s) return <p className="text-center py-20 text-slate-400">Der Sternenhimmel konnte nicht geladen werden.</p>;
  const shown = sel && sel.user_id !== user?.user_id ? sel : null;
  const renamed = (name) => setS({ ...s, my_star: { ...s.my_star, name }, stars: s.stars.map((x) => (x.user_id === user?.user_id ? { ...x, name } : x)) });
  return (
    <div className="space-y-6 max-w-3xl mx-auto" data-testid="sky-page">
      <div className="rounded-3xl overflow-hidden bg-[#0b0820] text-white shadow-xl shadow-indigo-950/30 fade-up">
        <div className="p-5 sm:p-6 space-y-2">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-pink-300">Gemeinschaftsprojekt</p>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold">{s.title}</h1>
          <p className="text-sm text-indigo-100">Nur gemeinsam bringen wir den Himmel zum Leuchten. Jedes Sternbild, das voll ist, schenkt <b className="text-amber-200">allen, die mitgeholfen haben, {s.bonus} Bonuspunkte</b>.</p>
        </div>
        <SkyCanvas sky={s} onStar={setSel} selected={sel?.user_id} meId={user?.user_id} />
        <div className="p-5 sm:p-6 space-y-3">
          {s.boost_until && <BoostBanner until={s.boost_until} />}
          <Progress s={s} />
          {shown && <StarInfo star={shown} />}
          {s.my_star ? <StarInfo star={s.my_star} mine onRenamed={renamed} /> : <p className="text-xs text-indigo-200" data-testid="sky-no-star">✦ Bau mit und du bekommst deinen eigenen Stern am Himmel, den du selbst benennen kannst. Tippe auf einen Stern, um zu sehen, wem er gehört.</p>}
          <p className="text-xs text-indigo-200 flex items-center gap-1.5" data-testid="sky-helpers"><Users className="w-3.5 h-3.5" /> {s.helpers} {s.helpers === 1 ? "Mitglied hilft" : "Mitglieder helfen"} mit · Dein Anteil: <b className="text-amber-200" data-testid="sky-my-share">{fmt(s.my.amount)} Staub ({s.my.share} %)</b></p>
          {s.finished && <p className="rounded-2xl bg-amber-200/15 border border-amber-200/30 p-3 text-sm text-amber-100" data-testid="sky-finished">Der Himmel ist komplett! 🌌 Danke an alle – bald startet ein neuer Sternenhimmel.</p>}
        </div>
      </div>
      {!s.finished && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <SkyDonate onDone={setS} />
          <div className="rounded-3xl bg-white border border-pink-100 p-5 space-y-3" data-testid="sky-invite">
            <p className="font-bold flex items-center gap-2"><UserPlus className="w-4 h-4 text-rose-500" /> Freunde einladen</p>
            <p className="text-sm text-slate-600">Jeder neue Freund, der sich über deinen Link registriert, bringt <b>{fmt(s.invite_value)} Sternenstaub</b> – ganz ohne Punkte. Und jede geschaffte Tagesmission bringt automatisch <b>{fmt(s.mission_value)} Sternenstaub</b>.</p>
            <Link to="/einladen" data-testid="sky-invite-link" className="h-10 px-4 rounded-full pink-gradient text-white text-sm font-bold inline-flex items-center gap-1.5"><UserPlus className="w-4 h-4" /> Jetzt einladen</Link>
          </div>
        </div>
      )}
      <div className="rounded-3xl bg-white border border-pink-100 overflow-hidden">
        <p className="p-5 pb-2 font-display text-xl font-extrabold flex items-center gap-2"><Trophy className="w-5 h-5 text-amber-500" /> Top-Baumeister</p>
        <Builders top={s.top} meId={user?.user_id} />
      </div>
      {s.recent.length > 0 && (
        <div className="rounded-3xl bg-white border border-pink-100 p-5 space-y-2" data-testid="sky-recent">
          <p className="font-bold">Zuletzt geleuchtet</p>
          {s.recent.map((r, i) => <p key={i} className="text-sm text-slate-600">✨ <b>{r.name}</b> {r.source === "invite" ? "hat einen Freund eingeladen" : r.source === "mission" ? "hat eine Tagesmission geschafft" : "hat Sternenstaub verschenkt"} · +{fmt(r.amount)}</p>)}
        </div>
      )}
    </div>
  );
}
